import React, { useState } from 'react';
import { XIcon, InfoIcon, DollarSignIcon, AlertCircleIcon } from 'lucide-react';
interface TruckingCompany {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  truckCount: number;
  rating: number;
  status: 'active' | 'inactive' | 'pending';
  preferredStatus?: boolean;
  rates?: {
    [key: string]: number;
  };
}
interface TruckingCompanyRatesModalProps {
  company: TruckingCompany;
  truckTypes: string[];
  onClose: () => void;
  onSave: (companyId: string, rates: {
    [key: string]: number;
  }) => void;
}
export const TruckingCompanyRatesModal = ({
  company,
  truckTypes,
  onClose,
  onSave
}: TruckingCompanyRatesModalProps) => {
  const [rates, setRates] = useState<{
    [key: string]: number;
  }>({
    ...company.rates
  });
  const [defaultRate, setDefaultRate] = useState(85.0);
  const [suppressedTypes, setSuppressedTypes] = useState<string[]>([]);
  const handleRateChange = (truckType: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setRates({
      ...rates,
      [truckType]: numValue
    });
  };
  const handleDefaultRateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 0;
    setDefaultRate(value);
  };
  const applyDefaultToAll = () => {
    const newRates = {
      ...rates
    };
    truckTypes.forEach(type => {
      if (!suppressedTypes.includes(type)) {
        newRates[type] = defaultRate;
      }
    });
    setRates(newRates);
  };
  const toggleSuppressType = (truckType: string) => {
    if (suppressedTypes.includes(truckType)) {
      setSuppressedTypes(suppressedTypes.filter(t => t !== truckType));
      // Restore the rate to default when unsuppressing
      setRates({
        ...rates,
        [truckType]: defaultRate
      });
    } else {
      setSuppressedTypes([...suppressedTypes, truckType]);
      // Set rate to 0 when suppressing
      setRates({
        ...rates,
        [truckType]: 0
      });
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(company.id, rates);
  };
  return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1025">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl h-[90vh] flex flex-col" data-id="element-1026">
        {/* Fixed Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0 bg-white rounded-t-lg" data-id="element-1027">
          <h3 className="text-lg font-medium" data-id="element-1028">Edit Rates for {company.name}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" data-id="element-1029">
            <XIcon size={20} data-id="element-1030" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col h-full min-h-0" data-id="element-1031">
          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto min-h-0" data-id="element-1032">
            <div className="p-6 space-y-4" data-id="element-1033">
              {/* Company Info and Default Rate Section */}
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-4 border-b" data-id="element-1034">
                <div data-id="element-1035">
                  <h4 className="font-medium" data-id="element-1036">{company.contactPerson}</h4>
                  <p className="text-sm text-gray-500" data-id="element-1037">{company.email}</p>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2" data-id="element-1038">
                  <label className="text-sm font-medium whitespace-nowrap" data-id="element-1039">
                    Default Rate:
                  </label>
                  <div className="flex items-center" data-id="element-1040">
                    <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md" data-id="element-1041">
                      $
                    </span>
                    <input type="number" value={defaultRate} onChange={handleDefaultRateChange} className="w-24 border border-gray-300 p-2" min="0" step="0.01" data-id="element-1042" />
                  </div>
                  <button type="button" onClick={applyDefaultToAll} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-sm whitespace-nowrap" data-id="element-1043">
                    Apply to All
                  </button>
                </div>
              </div>
              {/* Info Section */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3" data-id="element-1044">
                <div className="flex items-start gap-2" data-id="element-1045">
                  <InfoIcon size={18} className="text-blue-500 mt-0.5 flex-shrink-0" data-id="element-1046" />
                  <div className="text-sm text-blue-800" data-id="element-1047">
                    <p data-id="element-1048">
                      Set custom rates for each truck type that this trucking
                      company offers. Check "Suppress" to hide a truck type from
                      this company.
                    </p>
                  </div>
                </div>
              </div>
              {/* Rates Table */}
              <div className="border rounded-md overflow-hidden" data-id="element-1049">
                <div className="overflow-x-auto" data-id="element-1050">
                  <table className="w-full" data-id="element-1051">
                    <thead className="sticky top-0 z-10" data-id="element-1052">
                      <tr className="bg-gray-50" data-id="element-1053">
                        <th className="p-3 text-left whitespace-nowrap font-medium" data-id="element-1054">
                          Truck Type
                        </th>
                        <th className="p-3 text-left whitespace-nowrap font-medium" data-id="element-1055">
                          Rate ($/hour)
                        </th>
                        <th className="p-3 text-left whitespace-nowrap font-medium" data-id="element-1056">
                          Suppress
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" data-id="element-1057">
                      {truckTypes.map((truckType, index) => <tr key={truckType} className={`${suppressedTypes.includes(truckType) ? 'bg-gray-100' : index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`} data-id="element-1058">
                          <td className="p-3 whitespace-nowrap font-medium" data-id="element-1059">
                            {truckType}
                          </td>
                          <td className="p-3" data-id="element-1060">
                            <div className="flex items-center" data-id="element-1061">
                              <span className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-sm" data-id="element-1062">
                                $
                              </span>
                              <input type="number" value={rates[truckType] || 0} onChange={e => handleRateChange(truckType, e.target.value)} className="w-24 border border-gray-300 p-2 rounded-r-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500" min="0" step="0.01" disabled={suppressedTypes.includes(truckType)} data-id="element-1063" />
                            </div>
                          </td>
                          <td className="p-3" data-id="element-1064">
                            <label className="flex items-center cursor-pointer" data-id="element-1065">
                              <input type="checkbox" checked={suppressedTypes.includes(truckType)} onChange={() => toggleSuppressType(truckType)} className="mr-2 rounded focus:ring-2 focus:ring-blue-500" data-id="element-1066" />
                              <span className="text-sm" data-id="element-1067">Suppress</span>
                            </label>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
              {/* Warning for Suppressed Types */}
              {suppressedTypes.length > 0 && <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 p-3 rounded-md border border-amber-200" data-id="element-1068">
                  <AlertCircleIcon size={18} className="text-amber-500 flex-shrink-0 mt-0.5" data-id="element-1069" />
                  <div data-id="element-1070">
                    <p data-id="element-1071">
                      <strong data-id="element-1072">Note:</strong> Suppressed truck types will not be
                      available for this trucking company when assigning jobs.
                    </p>
                  </div>
                </div>}
              {/* Info Footer */}
              <div className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 p-3 rounded-md" data-id="element-1073">
                <DollarSignIcon size={18} className="text-gray-500 flex-shrink-0 mt-0.5" data-id="element-1074" />
                <div data-id="element-1075">
                  <p data-id="element-1076">
                    These rates will be used when calculating job costs for this
                    trucking company. You can update these rates at any time.
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Fixed Footer */}
          <div className="border-t px-6 py-4 flex justify-end gap-3 flex-shrink-0 bg-white rounded-b-lg" data-id="element-1077">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-1078">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors" data-id="element-1079">
              Save Rates
            </button>
          </div>
        </form>
      </div>
    </div>;
};